/**
 * 录单端权限码清单 —— 全仓库唯一真源。路由 `meta.perm`、`v-perm`、store 判定只允许引用这里，
 * 不允许在视图里再打一遍字符串（否则下一次改动又会漂移出第二套码）。
 *
 * 为什么必须收口（2026-09-11 联调取证）：本端此前自造了带端前缀的码
 * （`bpo:order:create` / `bpo:order:self-view` / `bpo:reject:appeal` / `bpo:help`）。真实后端
 * `GET /auth/profile` 给 `bpo.agent01` 下发的是**不带端前缀**的
 * `menuPermissions:["menu:order"]` + `buttonPermissions:["order:create","order:validate","order:appeal","order:proxy"]`
 * （观察于 commit `ba9cfb7`、builtAt 2026-09-10T23:40:49Z；原始响应体见
 * `shared/evidence/frontend-step5-bpo-web-20260911/profile-snapshot.json`）。
 * 后果不是「少个按钮」而是**整端打不开**：登录 200、令牌有效，路由守卫判缺码 → `#/forbidden`。
 * 端隔离本来就由 `endBinding` 承担，权限码里再塞一次端名属于重复建模，两边必然对不齐。
 *
 * 仍然存在的反向缺口（已回报，前端不做兜底放行）：
 * - `GET /permissions/tree` 实测返回 `[]`，服务端也没有可查询的权限码清单，本清单因此只能以
 *   persona 快照为真源逐条取证；权限树一旦补齐，本文件须与之比对并记录差异。
 * - 「存草稿」和「录入帮助」在快照里**没有专属码**。缺码时复用同族码而不是放开任意人：
 *   草稿与建单同属一个写族 → `order:create`；三条视图同属 `menu:order` 一个菜单 → `menu:order`
 *   （本端只有 BPO 客服一种业务身份，端绑定即边界）。若后续要求单独授权，改这一处即可。
 * - 快照另下发 `order:proxy`（代客录入），本端 P0 未放该入口：属于「多给了权限」而非前端漏实现，
 *   权限树补齐后须回看这一项。
 */
export const PERM = {
  /** 菜单级：录单端三条视图（新建订单 / 我的订单 / 录入帮助）同属订单菜单 */
  menuOrder: 'menu:order',
  /** 按钮级：四项校验通过后提交建单（`POST /bpo/orders`），草稿复用同一写族码 */
  orderCreate: 'order:create',
  /** 按钮级：显式触发 `POST /bpo/orders/validate` */
  orderValidate: 'order:validate',
  /**
   * 菜单级：BPO 机构管理员「全部订单」（v1.4 §5.5 前端 mock 档码形，夹具 `BpoMenus` 带 `menu:` 前缀）。
   * BPO_ADMIN 快照含这枚 ⇒ 导航分流按它判角色，⛔ 硬编码 roleCode 字符串。
   */
  menuBpoAllOrders: 'menu:bpo_all_orders',
  /** 菜单级：BPO 机构管理员「成员管理」（同前缀口径） */
  menuBpoTeam: 'menu:bpo_team',
  /** 按钮级/服务端过滤码：机构全量订单读腿（v1.4 §2.6，三段码形系群主 seq=809 批准首例） */
  agencyOrdersAll: 'agency:orders:all',
  /** 按钮级：管理机构员工（成员页写操作闸门） */
  agencyStaff: 'agency:staff',
  /** 按钮级：导出机构订单数据（v1.4 在册码；本端导出端点未在 19 枚清单内，暂只入权限集不长出入口） */
  agencyExport: 'agency:export',
} as const;
